import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    MessageBar,
    MessageBarBody,
    Spinner,
    Text,
    Badge,
    tokens,
    makeStyles,
    mergeClasses,
    shorthands,
} from "@fluentui/react-components";
import {
    ArrowUndo20Regular,
    ArrowCircleDown20Regular,
    Link20Regular,
    DismissCircle20Regular,
    Person20Regular,
    DocumentText20Regular,
    ArrowSync20Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
    MoneyHand24Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { PaymentImputation } from "../../../../models/Entities/CashFlow/Payment/PaymentImputation";
import * as ComponentFunctions from './PaymentImputations.Function';

// ────────────────────────────────────────────────────────────────────
// Movement type accent palette (subtle, professional)
// ────────────────────────────────────────────────────────────────────
const ACCENT = {
    pago: { color: "#0f7a3a", surface: "#f1faf4" },          // green
    imputacion: { color: "#0f548c", surface: "#eef5fb" },    // blue
    anulacion: { color: "#b3261e", surface: "#fdf3f2" },     // red
    default: { color: "#4a5568", surface: "#f5f6f7" },       // neutral
} as const;

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM,
        padding: `${tokens.spacingVerticalS} 0`,
    },

    // ── Header ──────────────────────────────────────────────────────
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacingHorizontalM,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    headerTitleWrap: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    headerIcon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        flexShrink: 0,
    },
    headerTitle: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
        lineHeight: "1.2",
    },
    headerSubtitle: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        lineHeight: "1.2",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        flexShrink: 0,
    },

    // ── List ────────────────────────────────────────────────────────
    list: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalS,
    },

    // ── Card ────────────────────────────────────────────────────────
    card: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: tokens.borderRadiusMedium,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        backgroundColor: tokens.colorNeutralBackground1,
        overflow: "hidden",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "0.15s",
        transitionTimingFunction: "ease",
        ":hover": {
            boxShadow: tokens.shadow4,
            ...shorthands.borderColor(tokens.colorNeutralStroke1),
        },
    },
    cardAccentBar: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: "3px",
        backgroundColor: ACCENT.default.color,
    },

    // ── Movement header ─────────────────────────────────────────────
    movementHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        paddingLeft: `calc(${tokens.spacingHorizontalL} + 3px)`,
        gap: tokens.spacingHorizontalM,
        cursor: "pointer",
        userSelect: "none",
        backgroundColor: tokens.colorNeutralBackground1,
        transitionProperty: "background-color",
        transitionDuration: "0.15s",
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    movementHeaderExpanded: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    chevron: {
        flexShrink: 0,
        color: tokens.colorNeutralForeground3,
        display: "inline-flex",
    },
    collapsibleBody: {
        overflow: "hidden",
        transitionProperty: "max-height, opacity",
        transitionDuration: "0.25s, 0.2s",
        transitionTimingFunction: "ease",
        maxHeight: "600px",
        opacity: "1",
    },
    collapsibleBodyHidden: {
        maxHeight: "0px",
        opacity: "0",
    },
    movementLeft: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalM,
        flex: 1,
        minWidth: 0,
    },
    movementIconWrap: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "36px",
        height: "36px",
        borderRadius: tokens.borderRadiusMedium,
        flexShrink: 0,
    },
    movementMeta: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        minWidth: 0,
    },
    movementTopLine: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
    },
    movementId: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: "tabular-nums",
    },
    movementType: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    movementAmountWrap: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "2px",
        flexShrink: 0,
    },
    movementAmount: {
        fontWeight: tokens.fontWeightBold,
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorNeutralForeground1,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.01em",
    },
    movementAmountSecondary: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: "tabular-nums",
    },

    // ── Card body ───────────────────────────────────────────────────
    cardBody: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    section: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalS,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    },
    sectionLeft: {
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
    },
    sectionTitleIcon: {
        display: "inline-flex",
        color: tokens.colorNeutralForeground3,
    },
    field: {
        display: "grid",
        gridTemplateColumns: "minmax(80px, 32%) 1fr",
        alignItems: "baseline",
        gap: tokens.spacingHorizontalS,
        paddingTop: "2px",
        paddingBottom: "2px",
    },
    fieldLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    fieldValue: {
        fontWeight: tokens.fontWeightMedium,
        fontSize: tokens.fontSizeBase200,
        fontFamily: tokens.fontFamilyMonospace,
        color: tokens.colorNeutralForeground1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    fieldValueAmount: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase200,
        fontVariantNumeric: "tabular-nums",
        color: tokens.colorNeutralForeground1,
    },
    fieldEmpty: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground4,
        fontStyle: "italic",
    },

    // ── Footer ──────────────────────────────────────────────────────
    cardFooter: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    exchangeRate: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: "tabular-nums",
    },
    exchangeRateLabel: {
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
    },

    // ── Empty / states ──────────────────────────────────────────────
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.spacingVerticalS,
        padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
        color: tokens.colorNeutralForeground3,
        border: `1px dashed ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    emptyStateIcon: {
        color: tokens.colorNeutralForeground4,
    },
    feedback: {
        marginTop: 0,
    },
});

type CardItemStyles = ReturnType<typeof useStyles>;

type AccentKey = keyof typeof ACCENT;

interface MovementConfig {
    icon: React.ReactElement;
    accent: AccentKey;
    badgeColor: "success" | "informative" | "danger" | "subtle";
}

interface CardItemProps {
    item: PaymentImputation;
    config: MovementConfig;
    hasDebt: boolean;
    styles: CardItemStyles;
    t: (key: string, fallback: string) => string;
    formatAmount: (amount: number, currency: string | null | undefined) => string;
    formatGuid: (guid: string | undefined) => string;
    isRollingBack: boolean;
    onRollback: (id: number) => void;
}

const CardItem = ({ item, config, hasDebt, styles, t, formatAmount, formatGuid, isRollingBack, onRollback }: CardItemProps) => {
    const [expanded, setExpanded] = useState(false);
    const accent = ACCENT[config.accent];

    return (
        <div className={styles.card}>
            {/* Accent bar (left edge, color by movement type) */}
            <span className={styles.cardAccentBar} style={{ backgroundColor: accent.color }} aria-hidden="true" />

            {/* Clickable header: icon + type + amount + chevron */}
            <div
                className={mergeClasses(styles.movementHeader, expanded ? styles.movementHeaderExpanded : undefined)}
                onClick={() => setExpanded(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded(prev => !prev);
                    }
                }}
            >
                <div className={styles.movementLeft}>
                    <div
                        className={styles.movementIconWrap}
                        style={{ backgroundColor: accent.surface, color: accent.color }}
                    >
                        {config.icon}
                    </div>
                    <div className={styles.movementMeta}>
                        <div className={styles.movementTopLine}>
                            <Badge appearance="tint" color={config.badgeColor} size="small">
                                {item.movementType ?? t('cashflow.imputations.unknownType', 'Desconocido')}
                            </Badge>
                            <span className={styles.movementId}>
                                #{item.movementId}
                            </span>
                        </div>
                        <span className={styles.movementType}>
                            {t('cashflow.imputations.imputationTitle', 'Imputación')} #{item.imputationId}
                        </span>
                    </div>
                </div>
                <div className={styles.movementAmountWrap}>
                    <span className={styles.movementAmount}>
                        {formatAmount(item.amountInPaymentCurrency, item.paymentCurrencyCode)}
                    </span>
                    {item.paymentCurrencyCode !== item.debtCurrencyCode && (
                        <span className={styles.movementAmountSecondary}>
                            ≈ {formatAmount(item.amountInDebtCurrency, item.debtCurrencyCode)}
                        </span>
                    )}
                </div>
                <span className={styles.chevron}>
                    {expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                </span>
            </div>

            {/* Collapsible body: details + footer */}
            <div className={mergeClasses(styles.collapsibleBody, expanded ? undefined : styles.collapsibleBodyHidden)}>

                <div className={styles.cardBody}>

                    {/* Payment section */}
                    <div className={mergeClasses(styles.section, styles.sectionLeft)}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.sectionTitleIcon}><Person20Regular /></span>
                            {t('cashflow.imputations.paymentParties', 'Pago')}
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                                {t('cashflow.imputations.payer', 'Pagador')}
                            </span>
                            <span
                                className={formatGuid(item.payment?.payer) === '—' ? styles.fieldEmpty : styles.fieldValue}
                                title={item.payment?.payer ?? ''}
                            >
                                {formatGuid(item.payment?.payer)}
                            </span>
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                                {t('cashflow.imputations.receiver', 'Cobrador')}
                            </span>
                            <span
                                className={formatGuid(item.payment?.receiver) === '—' ? styles.fieldEmpty : styles.fieldValue}
                                title={item.payment?.receiver ?? ''}
                            >
                                {formatGuid(item.payment?.receiver)}
                            </span>
                        </div>
                    </div>

                    {/* Debt section */}
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.sectionTitleIcon}><DocumentText20Regular /></span>
                            {hasDebt
                                ? `${t('cashflow.imputations.debtInfo', 'Deuda')} #${item.debtId}`
                                : t('cashflow.imputations.noDebt', 'Sin deuda')}
                        </div>
                        {hasDebt ? (
                            <>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>
                                        {t('cashflow.imputations.debtAmount', 'Monto')}
                                    </span>
                                    <span className={styles.fieldValueAmount}>
                                        {formatAmount(item.debt?.amount ?? 0, item.debtCurrencyCode)}
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>
                                        {t('cashflow.imputations.debtor', 'Deudor')}
                                    </span>
                                    <span
                                        className={formatGuid(item.debt?.debtor) === '—' ? styles.fieldEmpty : styles.fieldValue}
                                        title={item.debt?.debtor ?? ''}
                                    >
                                        {formatGuid(item.debt?.debtor)}
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>
                                        {t('cashflow.imputations.creditor', 'Acreedor')}
                                    </span>
                                    <span
                                        className={formatGuid(item.debt?.creditor) === '—' ? styles.fieldEmpty : styles.fieldValue}
                                        title={item.debt?.creditor ?? ''}
                                    >
                                        {formatGuid(item.debt?.creditor)}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <span className={styles.fieldEmpty}>—</span>
                        )}
                    </div>
                </div>

                {/* Footer: exchange rate + rollback action */}
                <div className={styles.cardFooter}>
                    {item.exchangeRate !== 1 ? (
                        <span className={styles.exchangeRate}>
                            <span className={styles.exchangeRateLabel}>
                                {t('cashflow.imputations.exchangeRate', 'TC')}
                            </span>
                            {item.exchangeRate.toFixed(4)}
                        </span>
                    ) : <span />}
                    <Button
                        appearance="subtle"
                        icon={<ArrowUndo20Regular />}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onRollback(item.imputationId); }}
                        disabled={isRollingBack}
                        title={t('cashflow.imputations.rollback', 'Deshacer')}
                    >
                        {t('cashflow.imputations.rollback', 'Deshacer')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const PaymentImputationsComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const styles = useStyles();
    const [confirmRollbackId, setConfirmRollbackId] = useState<number | null>(null);

    useEffect(() => {
        if (props.paymentId) {
            props.GetPaymentImputations(props.paymentId);
        }
    }, [props.paymentId]);

    const formatAmount = (amount: number, currency: string | null | undefined) =>
        `${amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim();

    const formatGuid = (guid: string | undefined): string => {
        if (!guid || guid === '00000000-0000-0000-0000-000000000000') return '—';
        return guid.substring(0, 8).toUpperCase() + '…';
    };

    const getMovementConfig = (type: string | null): MovementConfig => {
        switch (type) {
            case 'Pago':
                return { icon: <ArrowCircleDown20Regular />, accent: 'pago', badgeColor: 'success' };
            case 'Imputacion de Pago':
                return { icon: <Link20Regular />, accent: 'imputacion', badgeColor: 'informative' };
            case 'Anulacion de Movimiento':
                return { icon: <DismissCircle20Regular />, accent: 'anulacion', badgeColor: 'danger' };
            default:
                return { icon: <ArrowSync20Regular />, accent: 'default', badgeColor: 'subtle' };
        }
    };

    const renderCard = (item: PaymentImputation) => {
        const config = getMovementConfig(item.movementType);
        const hasDebt = item.debtId > 0;

        return (
            <CardItem
                key={item.imputationId}
                item={item}
                config={config}
                hasDebt={hasDebt}
                styles={styles}
                t={t}
                formatAmount={formatAmount}
                formatGuid={formatGuid}
                isRollingBack={props.isRollingBack}
                onRollback={(id) => setConfirmRollbackId(id)}
            />
        );
    };

    return (
        <div className={styles.root}>
            {props.showTitle !== false && (
                <div className={styles.header}>
                    <div className={styles.headerTitleWrap}>
                        <span className={styles.headerIcon} aria-hidden="true">
                            <MoneyHand24Regular />
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <span className={styles.headerTitle}>
                                {t('cashflow.imputations.title', 'Imputaciones del Pago')}
                            </span>
                            {!props.isLoadingImputations && (
                                <span className={styles.headerSubtitle}>
                                    {props.imputations.length}{' '}
                                    {props.imputations.length === 1
                                        ? t('cashflow.imputations.movementSingular', 'movimiento')
                                        : t('cashflow.imputations.movementPlural', 'movimientos')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={styles.headerActions}>
                        {props.isLoadingImputations && (
                            <Spinner size="extra-small" label={t('cashflow.imputations.loading', 'Cargando imputaciones...')} />
                        )}
                    </div>
                </div>
            )}

            {props.isLoadingImputations && props.showTitle === false && (
                <Spinner size="extra-small" label={t('cashflow.imputations.loading', 'Cargando imputaciones...')} />
            )}

            {props.failOnLoadingImputations && (
                <MessageBar intent="error" className={styles.feedback}>
                    <MessageBarBody>{props.error?.ErrorMessage ?? 'Error al cargar las imputaciones.'}</MessageBarBody>
                </MessageBar>
            )}

            {props.successRollingBack === true && (
                <MessageBar intent="success" className={styles.feedback}>
                    <MessageBarBody>{t('cashflow.imputations.rollbackSuccess', 'Imputación revertida correctamente.')}</MessageBarBody>
                </MessageBar>
            )}

            {props.failOnRollingBack && (
                <MessageBar intent="error" className={styles.feedback}>
                    <MessageBarBody>{t('cashflow.imputations.rollbackError', 'Error al revertir la imputación.')}</MessageBarBody>
                </MessageBar>
            )}

            {!props.isLoadingImputations && props.imputations.length === 0 ? (
                <div className={styles.emptyState}>
                    <span className={styles.emptyStateIcon}>
                        <DocumentText20Regular style={{ width: 32, height: 32 }} />
                    </span>
                    <Text weight="semibold">
                        {t('cashflow.imputations.noImputations', 'No hay imputaciones registradas para este pago.')}
                    </Text>
                </div>
            ) : (
                <div className={styles.list}>
                    {props.imputations.map(renderCard)}
                </div>
            )}

            {/* Rollback confirmation dialog */}
            <Dialog open={confirmRollbackId !== null} onOpenChange={(_, data) => { if (!data.open) setConfirmRollbackId(null); }}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>{t('cashflow.imputations.rollbackConfirmTitle', 'Confirmar Reversión')}</DialogTitle>
                        <DialogContent>
                            <Text>
                                {t('cashflow.imputations.rollbackConfirmMessage', '¿Está seguro de que desea revertir esta imputación? Esta acción no se puede deshacer.')}
                            </Text>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" onClick={() => setConfirmRollbackId(null)}>
                                    {t('cashflow.imputations.cancel', 'Cancelar')}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                icon={props.isRollingBack ? <Spinner size="extra-tiny" /> : <ArrowUndo20Regular />}
                                onClick={() => {
                                    if (confirmRollbackId !== null) {
                                        props.RollbackPaymentImputation(confirmRollbackId, props.paymentId);
                                        setConfirmRollbackId(null);
                                    }
                                }}
                                disabled={props.isRollingBack}
                            >
                                {t('cashflow.imputations.confirm', 'Confirmar')}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default ComponentFunctions.connector(PaymentImputationsComponent);
